/**
 * Money formatting for integer minor units (TMMPDM-0006, one format module since TMMPDM-0065,
 * TMM-B1). tmm-api sends every amount as an integer in the currency's minor unit (cents for
 * USD/CRC/MXN, yen for JPY); the exponent comes from Intl's knowledge of the currency, so this
 * never carries a currency table. Formatting only — never arithmetic that changes a value the
 * server sent, and never a sum across currencies.
 *
 * A value the API didn't send (absent, null or undefined) shows "—", never 0 (RPT-005); a sent 0
 * shows 0.
 */

/** What shows for a value the API didn't send. */
export const NOT_SENT = '—';

export type Maybe<T> = T | null | undefined;

export function minorUnitDigits(currency: string, locale = 'en'): number {
  try {
    return (
      new Intl.NumberFormat(locale, { style: 'currency', currency }).resolvedOptions()
        .maximumFractionDigits ?? 2
    );
  } catch {
    return 2;
  }
}

/** A plain number in the locale's separators, no currency: what shows when Intl can't format the currency code. */
export function formatDecimal(
  value: number,
  locale: string,
  minDigits: number,
  maxDigits: number,
): string {
  const options = { minimumFractionDigits: minDigits, maximumFractionDigits: maxDigits };
  try {
    return new Intl.NumberFormat(locale, options).format(value);
  } catch {
    return new Intl.NumberFormat('en', options).format(value);
  }
}

interface CurrencyOptions {
  minDigits: number;
  maxDigits: number;
  signed?: boolean;
}

function formatCurrency(
  value: number,
  currency: string,
  locale: string,
  { minDigits, maxDigits, signed = false }: CurrencyOptions,
): string {
  const fallback = () => `${formatDecimal(value, locale, minDigits, maxDigits)} ${currency}`;
  try {
    const formatted = new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
      minimumFractionDigits: minDigits,
      maximumFractionDigits: maxDigits,
      ...(signed ? { signDisplay: 'exceptZero' as const } : {}),
    }).format(value);
    // ISO "no currency" (XXX) and similar codes format as ¤ without throwing.
    return formatted.includes('¤') ? fallback() : formatted;
  } catch {
    return fallback();
  }
}

/** Integer minor units → the currency's own format; "—" when the API didn't send it. */
export function formatMoney(minor: Maybe<number>, currency: string, locale: string): string {
  if (minor === null || minor === undefined) return NOT_SENT;
  const digits = minorUnitDigits(currency, locale);
  return formatCurrency(minor / 10 ** digits, currency, locale, {
    minDigits: digits,
    maxDigits: digits,
  });
}

/**
 * A signed amount: "+" for a credit, Intl's minus for a debit, nothing for zero; "—" when the API
 * didn't send it. The explicit "+" is only for lists that mix debits and credits (settlement and
 * statement lines, TMM-B1) — everywhere else money is `formatMoney`.
 */
export function signedOrDash(minor: Maybe<number>, currency: string, locale: string): string {
  if (minor === null || minor === undefined) return NOT_SENT;
  const digits = minorUnitDigits(currency, locale);
  return formatCurrency(minor / 10 ** digits, currency, locale, {
    minDigits: digits,
    maxDigits: digits,
    signed: true,
  });
}

/**
 * Accounting style (TMMPDM-0089): a negative amount in parentheses, without a minus, so a cost reads
 * as a cost without relying on colour; zero and positives are `formatMoney`. Formatting only.
 */
export function formatMoneyAccounting(
  minor: Maybe<number>,
  currency: string,
  locale: string,
): string {
  if (minor === null || minor === undefined) return NOT_SENT;
  if (minor >= 0) return formatMoney(minor, currency, locale);
  return `(${formatMoney(-minor, currency, locale)})`;
}

/** Whole-unit rendering for tiles and charts: no fraction digits. */
export function formatMoneyWhole(minor: Maybe<number>, currency: string, locale: string): string {
  if (minor === null || minor === undefined) return NOT_SENT;
  const digits = minorUnitDigits(currency, locale);
  const value = minor / 10 ** digits;
  try {
    const formatted = new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
    if (formatted.includes('¤')) return `${Math.round(value)} ${currency}`;
    return formatted;
  } catch {
    return `${Math.round(value)} ${currency}`;
  }
}

function formatMicroCurrency(
  micro: number,
  currency: string,
  locale: string,
  minDigits: number,
  maxDigits: number,
): string {
  const value = micro / 1_000_000;
  const fallback = () => {
    const fractionDigits = Math.max(minorUnitDigits(currency, locale), 4);
    return `${formatDecimal(value, locale, fractionDigits, fractionDigits)} ${currency}`;
  };
  try {
    const formatted = new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
      minimumFractionDigits: minDigits,
      maximumFractionDigits: maxDigits,
    }).format(value);
    return formatted.includes('¤') ? fallback() : formatted;
  } catch {
    return fallback();
  }
}

/** Micro-units of currency → currency with up to 4 decimals; "—" when the API didn't send it. */
export function formatMicro(micro: Maybe<number>, currency: string, locale: string): string {
  if (micro === null || micro === undefined) return NOT_SENT;
  return formatMicroCurrency(micro, currency, locale, minorUnitDigits(currency, locale), 4);
}

/** Micro-units of currency per million tokens → currency with up to 4 decimals. */
export function formatMicroPerMtok(micro: Maybe<number>, currency: string, locale: string): string {
  return formatMicro(micro, currency, locale);
}

/** The one `micro → currency` conversion at a fixed 4 decimals (statement line unit price). */
export function formatUnitMicro(
  unitMicro: Maybe<number>,
  currency: string,
  locale: string,
): string {
  if (unitMicro === null || unitMicro === undefined) return NOT_SENT;
  return formatMicroCurrency(unitMicro, currency, locale, 4, 4);
}

/** Minor units → the major-unit string a money field is prefilled with (input conversion; 0009). */
export function majorFromMinor(minor: number, currency: string): string {
  const digits = minorUnitDigits(currency);
  return new Intl.NumberFormat('en-US', {
    useGrouping: false,
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(minor / 10 ** digits);
}

/**
 * A positive major-unit string a person typed → exactly those integer minor units, or `null` when it
 * isn't one (TMMPDM-0095: money that moves is never rounded). Digits with at most the currency's
 * fraction digits after a dot; no sign, no grouping. Built from the digits, never through a float.
 */
export function exactMinorFromMajor(major: string, currency: string): number | null {
  const digits = minorUnitDigits(currency);
  const match = /^(\d+)(?:\.(\d*))?$/.exec(major.trim());
  if (!match) return null;
  const fraction = match[2] ?? '';
  if (fraction.length > digits) return null;
  const minor = Number(`${match[1] ?? ''}${fraction.padEnd(digits, '0')}`);
  return Number.isSafeInteger(minor) && minor > 0 ? minor : null;
}

/** A major-unit string a person typed → integer minor units (input conversion only). */
export function majorToMinor(major: string, currency: string): number {
  const digits = minorUnitDigits(currency);
  const n = Number(major);
  if (!Number.isFinite(n)) return 0;
  return Math.round(n * 10 ** digits);
}
